import { zodResolver } from '@hookform/resolvers/zod';
import { lazy } from 'react';
import { useForm } from 'react-hook-form';
import { lazily } from 'react-lazily';

import { PostFriendFamilyNewRequest } from 'services/idm';
import { postFriendFamilyNewRequestSchema } from 'services/idm/post/schemas/post-friends-family-new.schema';

import { useDebounce } from 'hooks/use-debounce';
import { useScrollTo } from 'hooks/use-scroll-to';
import { useAppTranslation } from 'hooks/use-translates';

import styles from '@login/pages/friends-and-family/friends-and-family.module.scss';

const Button = lazy(() => import('core/generic/elements/button'));
const BirthSelection = lazy(
	() => import('@login/components/birth-selection/birth-selection')
);
const InfoRequiredFields = lazy(
	() => import('@login/components/info-required-fields/info-required-fields')
);
const { Insert } = lazily(() => import('core/generic/elements/form'));

const DEFAULT_VALUES = {
	birthYear: undefined,
	birthMonth: undefined,
	birthDay: undefined,
	firstName: '',
	lastName: '',
};

type FriendsAndFamilyNewFormProps = {
	onSubmit: (payload: PostFriendFamilyNewRequest) => Promise<void>;
	isSubmitting: boolean;
};

const FriendsAndFamilyNewForm = ({
	onSubmit,
	isSubmitting,
}: FriendsAndFamilyNewFormProps) => {
	const { t } = useAppTranslation();
	const { scrollToTop } = useScrollTo();

	const {
		control,
		trigger,
		handleSubmit,
		formState: { errors },
		register,
		reset,
	} = useForm<PostFriendFamilyNewRequest>({
		resolver: zodResolver(postFriendFamilyNewRequestSchema),
		mode: 'onTouched',
		defaultValues: DEFAULT_VALUES,
	});

	const handleOnSubmit = useDebounce(
		async (payload: PostFriendFamilyNewRequest) => {
			await onSubmit?.(payload).finally(() => {
				reset(DEFAULT_VALUES);
			});

			scrollToTop();
		},
		500
	);

	return (
		<form onSubmit={handleSubmit(handleOnSubmit)} noValidate={true}>
			<div className={styles['content-wrapper']}>
				<InfoRequiredFields />

				<div className={styles['form-box-content']}>
					<p className="body-small bold">
						{t('friends_and_family.under_age_13_label')}
					</p>

					<p className="body-small">
						{t('friends_and_family.under_age_13_description')}
					</p>
				</div>

				<BirthSelection
					control={control}
					trigger={trigger}
					errors={errors}
					required={true}
					hint={t('fullregister.day_help_full')}
					t={t}
				/>

				<div className={styles['form-box-content']}>
					<Insert
						label={t('fullregister.first_name_label_full')}
						error={t(errors.firstName?.message as string)}
						errorIcon="AlertCircleFilled"
						required={true}
						{...register('firstName')}
					/>

					<Insert
						label={t('fullregister.last_name_label_full')}
						error={t(errors.lastName?.message as string)}
						errorIcon="AlertCircleFilled"
						required={true}
						{...register('lastName')}
					/>
				</div>

				<Button
					type="submit"
					variant="primary"
					fullWidth
					isLoading={isSubmitting}
					disabled={isSubmitting}
					dimension="large"
					label={t('common.create_account_button')}
				/>
			</div>
		</form>
	);
};

export default FriendsAndFamilyNewForm;
